/**
 * "Write a guide" on the Help questions page: a link that opens GitHub's new
 * file form on the repository with a guide started from a group of questions,
 * ready to become a pull request. The guide checks in the frontend suite
 * still apply to what is merged; this only saves the person the blank page.
 */

import type { HelpQuestionGroup } from "@/services/admin";

const REPO = "https://github.com/Challaris/charisol-spark-backend";

/** The areas the Help panel lists, in its own order (docs/help/<area>/). */
export const GUIDE_AREAS = [
  "studio",
  "message-and-posts",
  "ideas",
  "research",
  "context-and-layers",
  "voice-and-persona",
  "tasks-and-workflows",
  "writer",
  "publishing-and-platforms",
  "discover",
  "billing-and-plans",
  "account",
] as const;

function slugOf(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .split("-")
      .slice(0, 8)
      .join("-") || "new-guide"
  );
}

/** The guide's title: the group's latest wording, without its question mark. */
export function guideTitleFor(group: HelpQuestionGroup): string {
  const text = group.question.replace(/\s+/g, " ").replace(/[?]+$/, "").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Search words for the guide: the terms the group's questions share. */
export function guideKeywordsFor(group: HelpQuestionGroup): string[] {
  return group.groupKey === "other"
    ? []
    : group.groupKey.split("-").filter(Boolean);
}

export function guideTemplate(group: HelpQuestionGroup, today: Date): string {
  const keywords = guideKeywordsFor(group).join(", ");
  return `---
title: ${guideTitleFor(group)}
area: studio # one of: ${GUIDE_AREAS.join(", ")}
keywords: [${keywords}]
feature: studio # the file name in docs/technical_documentation/features that this describes
plan: free
updated: ${today.toISOString().slice(0, 10)}
---

Write the answer here in under 200 words. One job: answer "${group.question.replace(/"/g, "'")}".

![Describe the screenshot](screenshot:${slugOf(group.question)})

Related: [Another guide](another-guide-slug)
`;
}

/** The GitHub link that opens the form with the template filled in. */
export function writeGuideUrl(group: HelpQuestionGroup, today: Date): string {
  const slug = slugOf(guideTitleFor(group));
  const params = new URLSearchParams({
    filename: `docs/help/studio/${slug}.md`,
    value: guideTemplate(group, today),
    quick_pull: "1",
    message: `Add help guide: ${guideTitleFor(group)}`,
    target_branch: `docs/help-${slug}`,
  });
  return `${REPO}/new/dev?${params.toString()}`;
}
